import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Database, 
  ChevronRight, 
  ChevronDown, 
  Check, 
  Eye, 
  RefreshCw, 
  Pencil, 
  Trash2, 
  Info,
  Search,
  ArrowLeft,
  ArrowRight,
  Loader2,
  Link,
  Server,
  HardDrive,
  User,
  Lock,
  Tag,
  Settings,
  MoreVertical,
  Table,
  Clock
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';

// Data connector definitions
const dataConnectors = [
  {
    id: 'upload',
    name: 'Upload File',
    icon: 'https://cdn-icons-png.flaticon.com/128/3143/3143609.png',
    description: 'CSV, Excel, JSON'
  },
  {
    id: 'snowflake',
    name: 'Snowflake',
    icon: 'https://companieslogo.com/img/orig/SNOW-35164165.png',
    description: 'Data warehouse'
  },
  {
    id: 'gcs',
    name: 'Google Cloud',
    icon: 'https://cdn.worldvectorlogo.com/logos/google-cloud-2.svg',
    description: 'GCS buckets'
  },
  {
    id: 'databricks',
    name: 'Databricks',
    icon: 'https://asset.brandfetch.io/idDwd0nn9u/idm22uhHUb.png',
    description: 'Lakehouse'
  },
  {
    id: 'postgresql',
    name: 'PostgreSQL',
    icon: 'https://cdn-icons-png.flaticon.com/128/5968/5968342.png',
    description: 'Database'
  },
  {
    id: 's3',
    name: 'Amazon S3',
    icon: 'https://cdn.worldvectorlogo.com/logos/aws-s3.svg',
    description: 'S3 buckets'
  },
  {
    id: 'azure',
    name: 'Azure Data Lake',
    icon: 'https://swimburger.net/media/ppnn3pcl/azure.png',
    description: 'ADLS Gen2'
  },
  {
    id: 'synapse',
    name: 'Azure Synapse',
    icon: 'https://swimburger.net/media/ppnn3pcl/azure.png',
    description: 'Analytics'
  }
];

// Mock schema/table data
const mockSchemas = [
  {
    name: 'sales_db',
    tables: [
      { name: 'customers', rows: '2.3M', columns: 12 },
      { name: 'orders', rows: '890K', columns: 18 },
      { name: 'order_items', rows: '4.1M', columns: 8 },
      { name: 'products', rows: '1.2K', columns: 15 },
      { name: 'returns', rows: '45K', columns: 10 },
      { name: 'shipments', rows: '230K', columns: 12 },
      { name: 'invoices', rows: '1.5M', columns: 14 },
      { name: 'payments', rows: '980K', columns: 9 },
      { name: 'refunds', rows: '32K', columns: 11 },
      { name: 'discounts', rows: '15K', columns: 6 },
      { name: 'promotions', rows: '8K', columns: 10 },
      { name: 'vendors', rows: '2.1K', columns: 16 },
      { name: 'warehouses', rows: '450', columns: 8 },
      { name: 'inventory', rows: '780K', columns: 13 },
      { name: 'categories', rows: '1.2K', columns: 5 },
      { name: 'regions', rows: '350', columns: 7 },
      { name: 'sales_reps', rows: '5.6K', columns: 12 },
      { name: 'territories', rows: '890', columns: 6 }
    ]
  },
  {
    name: 'marketing',
    tables: [
      { name: 'campaigns', rows: '45K', columns: 8 },
      { name: 'leads', rows: '120K', columns: 14 },
      { name: 'conversions', rows: '89K', columns: 6 },
      { name: 'ad_spend', rows: '34K', columns: 9 },
      { name: 'email_templates', rows: '2.3K', columns: 7 },
      { name: 'subscribers', rows: '890K', columns: 11 },
      { name: 'click_events', rows: '4.5M', columns: 8 },
      { name: 'ab_tests', rows: '1.2K', columns: 15 },
      { name: 'audiences', rows: '45K', columns: 9 },
      { name: 'channels', rows: '120', columns: 6 }
    ]
  },
  {
    name: 'analytics',
    tables: [
      { name: 'user_events', rows: '1.2M', columns: 24 },
      { name: 'page_views', rows: '3.4M', columns: 12 },
      { name: 'sessions', rows: '890K', columns: 18 },
      { name: 'funnels', rows: '23K', columns: 10 },
      { name: 'cohorts', rows: '5.6K', columns: 8 },
      { name: 'metrics_daily', rows: '120K', columns: 32 },
      { name: 'metrics_hourly', rows: '2.8M', columns: 32 },
      { name: 'dashboards', rows: '450', columns: 12 },
      { name: 'reports', rows: '8.9K', columns: 15 }
    ]
  },
  {
    name: 'staging',
    tables: [
      { name: 'raw_transactions', rows: '5.6M', columns: 32 },
      { name: 'temp_users', rows: '100K', columns: 8 },
      { name: 'import_logs', rows: '45K', columns: 6 },
      { name: 'validation_errors', rows: '12K', columns: 10 }
    ]
  },
  {
    name: 'finance',
    tables: [
      { name: 'accounts', rows: '12K', columns: 14 },
      { name: 'transactions', rows: '3.2M', columns: 18 },
      { name: 'budgets', rows: '890', columns: 12 },
      { name: 'forecasts', rows: '2.3K', columns: 16 },
      { name: 'expenses', rows: '450K', columns: 11 },
      { name: 'revenue', rows: '890K', columns: 9 },
      { name: 'cost_centers', rows: '120', columns: 7 },
      { name: 'journal_entries', rows: '1.8M', columns: 14 },
      { name: 'tax_records', rows: '230K', columns: 20 },
      { name: 'audit_logs', rows: '5.6M', columns: 8 }
    ]
  },
  {
    name: 'hr',
    tables: [
      { name: 'employees', rows: '15K', columns: 24 },
      { name: 'departments', rows: '120', columns: 8 },
      { name: 'positions', rows: '450', columns: 10 },
      { name: 'payroll', rows: '180K', columns: 16 },
      { name: 'benefits', rows: '45K', columns: 12 },
      { name: 'attendance', rows: '2.3M', columns: 6 },
      { name: 'performance_reviews', rows: '89K', columns: 18 },
      { name: 'training_records', rows: '34K', columns: 9 }
    ]
  },
  {
    name: 'operations',
    tables: [
      { name: 'work_orders', rows: '560K', columns: 14 },
      { name: 'equipment', rows: '8.9K', columns: 16 },
      { name: 'maintenance_logs', rows: '230K', columns: 10 },
      { name: 'suppliers', rows: '3.4K', columns: 12 },
      { name: 'purchase_orders', rows: '120K', columns: 18 },
      { name: 'quality_checks', rows: '890K', columns: 8 }
    ]
  }
];

// Mock existing connections
const initialConnections = [
  {
    id: 'conn-1',
    connectionName: 'Sales Analytics',
    dataSource: 'Snowflake',
    icon: 'https://companieslogo.com/img/orig/SNOW-35164165.png',
    createdBy: 'John Doe',
    createdDate: '2025-01-15',
    tables: {
      completed: 10,
      running: 2,
      failed: 0
    }
  },
  {
    id: 'conn-2',
    connectionName: 'Marketing Data',
    dataSource: 'Databricks',
    icon: 'https://asset.brandfetch.io/idDwd0nn9u/idm22uhHUb.png',
    createdBy: 'Jane Smith',
    createdDate: '2025-01-10',
    tables: {
      completed: 6,
      running: 1,
      failed: 1
    }
  },
  {
    id: 'conn-3',
    connectionName: 'Product Inventory',
    dataSource: 'PostgreSQL',
    icon: 'https://cdn-icons-png.flaticon.com/128/5968/5968342.png',
    createdBy: 'John Doe',
    createdDate: '2025-01-08',
    tables: {
      completed: 4,
      running: 0,
      failed: 0
    }
  }
];

const DataConnectionsPage = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [selectedConnector, setSelectedConnector] = useState(null);
  const [isConnecting, setIsConnecting] = useState(false);
  const [connectionSuccess, setConnectionSuccess] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedSchemas, setExpandedSchemas] = useState(['sales_db']);
  const [selectedTables, setSelectedTables] = useState([]);
  const [leftPanelWidth, setLeftPanelWidth] = useState(30);
  const [isDragging, setIsDragging] = useState(false);
  const [connections, setConnections] = useState(initialConnections);
  const [credentials, setCredentials] = useState({
    connectionName: '',
    accountUrl: '',
    warehouse: '',
    database: '',
    username: '',
    password: ''
  });

  // Step definitions
  const steps = [
    { number: 1, label: 'Connections', completed: currentStep > 1, sublabel: selectedConnector?.name },
    { number: 2, label: 'Configuration', completed: currentStep > 2, sublabel: connectionSuccess ? credentials.connectionName : null },
    { number: 3, label: 'Datasets', completed: false, sublabel: null }
  ];

  const handleConnectorSelect = (connector) => {
    setSelectedConnector(connector);
    setCurrentStep(2);
  };

  const handleEditConnection = (connection) => {
    // Find the connector details from dataConnectors
    const connector = dataConnectors.find(c => 
      c.name.toLowerCase() === connection.dataSource.toLowerCase()
    );
    
    // Set the connector and connection details
    setSelectedConnector(connector || { name: connection.dataSource, icon: connection.icon });
    setCredentials({
      ...credentials,
      connectionName: connection.connectionName
    });
    setConnectionSuccess(true);
    setCurrentStep(3);
  };

  const handleCredentialChange = (e) => {
    setCredentials({
      ...credentials,
      [e.target.name]: e.target.value
    });
  };

  const handleTestConnection = async () => {
    setIsConnecting(true);
    await new Promise(resolve => setTimeout(resolve, 2000));
    setIsConnecting(false);
    setConnectionSuccess(true);
  };

  const handleContinueToTables = () => {
    setCurrentStep(3);
  };

  const toggleSchema = (schemaName) => {
    setExpandedSchemas(prev => 
      prev.includes(schemaName) 
        ? prev.filter(s => s !== schemaName)
        : [...prev, schemaName]
    );
  };

  const toggleTableSelection = (schemaName, tableName) => {
    const tableId = `${schemaName}.${tableName}`;
    setSelectedTables(prev => 
      prev.some(t => t.id === tableId)
        ? prev.filter(t => t.id !== tableId)
        : [...prev, { 
            id: tableId, 
            schema: schemaName, 
            name: tableName, 
            alias: tableName,
            ...mockSchemas.find(s => s.name === schemaName)?.tables.find(t => t.name === tableName)
          }]
    );
  };

  const isTableSelected = (schemaName, tableName) => {
    return selectedTables.some(t => t.id === `${schemaName}.${tableName}`);
  };

  const getSelectedCountForSchema = (schemaName) => {
    return selectedTables.filter(t => t.schema === schemaName).length;
  };

  const handleSaveConnection = () => {
    // Create new connection object
    const newConnection = {
      id: `conn-${Date.now()}`,
      connectionName: credentials.connectionName || `${selectedConnector?.name} Connection`,
      dataSource: selectedConnector?.name,
      icon: selectedConnector?.icon,
      createdBy: 'Current User',
      createdDate: new Date().toISOString().split('T')[0],
      tables: {
        completed: selectedTables.length,
        running: 0,
        failed: 0
      }
    };
    
    // Add to connections list
    setConnections(prev => [...prev, newConnection]);
    
    // Reset form state for next connection
    setSelectedConnector(null);
    setConnectionSuccess(false);
    setSelectedTables([]);
    setCredentials({
      connectionName: '',
      accountUrl: '',
      warehouse: '',
      database: '',
      username: '',
      password: ''
    });
    
    // Return to Step 1
    setCurrentStep(1);
  };

  const handleContinueToGraph = () => {
    navigate('/app/session');
  };

  // Drag handlers for resizable panels
  const handleMouseDown = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleMouseMove = (e) => {
    if (!isDragging) return;
    const container = e.currentTarget;
    const containerRect = container.getBoundingClientRect();
    const newWidth = ((e.clientX - containerRect.left) / containerRect.width) * 100;
    if (newWidth >= 20 && newWidth <= 70) {
      setLeftPanelWidth(newWidth);
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const filteredSchemas = mockSchemas.map(schema => ({
    ...schema,
    tables: schema.tables.filter(table => 
      searchTerm === '' || 
      table.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      schema.name.toLowerCase().includes(searchTerm.toLowerCase())
    )
  })).filter(schema => schema.tables.length > 0);

  return (
    <div className="h-full bg-slate-900 flex flex-col overflow-hidden">
      {/* Fixed Stepper Bar */}
      <div className="bg-slate-950/50 border-b border-slate-800/50 py-5 flex-shrink-0">
        <div className="flex items-center justify-between px-8">
          {/* Spacer for balance */}
          <div className="w-48"></div>
          
          {/* Centered Steps */}
          <div className="flex items-center">
            {steps.map((step, index) => (
              <React.Fragment key={step.number}>
                <div 
                  className={`flex items-center cursor-pointer ${currentStep >= step.number ? 'opacity-100' : 'opacity-40'}`}
                  onClick={() => step.completed && setCurrentStep(step.number)}
                >
                  <div className={`
                    w-9 h-9 rounded-full flex items-center justify-center text-sm font-semibold
                    ${currentStep === step.number 
                      ? 'bg-blue-500/20 text-blue-400 border border-blue-500/50' 
                      : step.completed 
                        ? 'bg-blue-600 text-white' 
                        : 'bg-slate-800 text-gray-500 border border-slate-700'}
                  `}>
                    {step.completed ? <Check className="w-4 h-4" /> : step.number}
                  </div>
                  <div className="ml-3 mr-3">
                    <span className={`text-sm font-medium ${currentStep === step.number ? 'text-white' : 'text-gray-400'}`}>
                      {step.label}
                    </span>
                    {step.sublabel && (
                      <span className="text-xs text-blue-400 block">{step.sublabel}</span>
                    )}
                  </div>
                </div>
                
                {index < steps.length - 1 && (
                  <div className={`w-20 h-[2px] mx-3 ${currentStep > step.number ? 'bg-blue-500/50' : 'bg-slate-700'}`} />
                )}
              </React.Fragment>
            ))}
          </div>
          
          {/* Continue to Graph Button - Right side, only in Step 1 with connections */}
          <div className="w-48 flex justify-end">
            {currentStep === 1 && connections.length > 0 && (
              <Button
                onClick={handleContinueToGraph}
                className="bg-blue-600 hover:bg-blue-700 text-white"
              >
                Continue to Semantic Graph
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex flex-col bg-slate-900 min-h-0 overflow-hidden">
        
        {/* Step 1: Data Sources - Left Aligned */}
        {currentStep === 1 && (
          <div className="flex-1 flex flex-col p-8 overflow-y-auto">
            
            {/* Existing Connections Section */}
            {connections.length > 0 && (
              <div className="mb-8">
                <div className="mb-4">
                  <h2 className="text-lg font-semibold text-white mb-1">Your Connections</h2>
                  <p className="text-gray-400 text-sm">Manage your existing data connections</p>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  {connections.map((connection) => (
                    <div
                      key={connection.id}
                      className="bg-slate-800/40 backdrop-blur-lg border border-slate-700/20 hover:bg-slate-800/50 transition-all duration-300 cursor-pointer group p-5 rounded-xl relative"
                    >
                      {/* Header: Data Source + Actions */}
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2">
                          <div className="w-9 h-9 rounded-lg bg-slate-800/50 flex items-center justify-center border border-slate-700/30">
                            <img
                              src={connection.icon}
                              alt={connection.dataSource}
                              className="w-5 h-5 object-contain"
                              onError={(e) => {
                                e.target.style.display = 'none';
                              }}
                            />
                          </div>
                          <span className="text-gray-300 font-medium text-sm">{connection.dataSource}</span>
                        </div>
                        
                        {/* Action Buttons */}
                        <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                          <button 
                            className="p-1.5 rounded hover:bg-slate-700 text-gray-400 hover:text-white transition-colors"
                            title="Edit"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleEditConnection(connection);
                            }}
                          >
                            <Pencil className="w-3.5 h-3.5" />
                          </button>
                          <button 
                            className="p-1.5 rounded hover:bg-red-500/20 text-gray-400 hover:text-red-400 transition-colors"
                            title="Delete"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </div>
                      
                      {/* Connection Name */}
                      <h3 className="text-white font-semibold text-base mb-3">
                        {connection.connectionName}
                      </h3>
                      
                      {/* Meta Info */}
                      <div className="flex items-center gap-4 text-xs text-gray-400 mb-4">
                        <span className="flex items-center gap-1.5">
                          <User className="w-3.5 h-3.5" />
                          {connection.createdBy}
                        </span>
                        <span className="flex items-center gap-1.5">
                          <Clock className="w-3.5 h-3.5" />
                          {new Date(connection.createdDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                        </span>
                      </div>
                      
                      {/* Datasets Status */}
                      <div className="pt-3 border-t border-slate-700/30">
                        <span className="text-xs text-gray-500 mb-2 block">Datasets</span>
                        <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5">
                          <span className="flex items-center gap-1.5 text-xs">
                            <div className="w-2 h-2 rounded-full bg-green-500"></div>
                            <span className="text-gray-300">{connection.tables.completed}</span>
                            <span className="text-gray-500">completed</span>
                          </span>
                          <span className="flex items-center gap-1.5 text-xs">
                            <div className="w-2 h-2 rounded-full bg-blue-500 animate-pulse"></div>
                            <span className="text-gray-300">{connection.tables.running}</span>
                            <span className="text-gray-500">running</span>
                          </span>
                          <span className="flex items-center gap-1.5 text-xs">
                            <div className="w-2 h-2 rounded-full bg-red-500"></div>
                            <span className="text-gray-300">{connection.tables.failed}</span>
                            <span className="text-gray-500">failed</span>
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {/* Divider */}
            {connections.length > 0 && (
              <div className="border-t border-slate-700/30 mb-8"></div>
            )}
            
            {/* Add New Connection Section */}
            <div>
              <div className="mb-4">
                <h2 className="text-lg font-semibold text-white mb-1">
                  {connections.length > 0 ? 'Add New Connection' : 'Select a Data Source'}
                </h2>
                <p className="text-gray-400 text-sm">Choose the type of data source you want to connect</p>
              </div>
              
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-6">
                {dataConnectors.map((connector) => (
                  <div
                    key={connector.id}
                    onClick={() => handleConnectorSelect(connector)}
                    className="bg-slate-800/40 backdrop-blur-lg border border-slate-700/20 hover:bg-slate-800/50 hover:border-blue-500/30 transition-all duration-300 hover:scale-105 cursor-pointer group p-6 rounded-xl relative"
                  >
                    {/* Hover Arrow Indicator - Top Right */}
                    <div className="absolute top-4 right-4 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                      <ArrowRight className="w-5 h-5 text-blue-400" />
                    </div>
                    <div className="flex flex-col items-center text-center space-y-4">
                      <div className="w-16 h-16 rounded-xl bg-slate-800/50 flex items-center justify-center group-hover:bg-slate-800/70 transition-all duration-300 border border-slate-700/30">
                        <img
                          src={connector.icon}
                          alt={connector.name}
                          className="w-10 h-10 object-contain"
                          onError={(e) => {
                            e.target.style.display = 'none';
                          }}
                        />
                      </div>
                      <div>
                        <h3 className="text-white font-semibold text-lg mb-2">
                          {connector.name}
                        </h3>
                        <p className="text-gray-500 text-sm">
                          {connector.description}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Step 2: Credentials - Centered */}
        {currentStep === 2 && (
          <div className="flex-1 flex flex-col items-center justify-center p-6">
            <div className="w-full max-w-xl">
              {/* Card */}
              <div className="backdrop-blur-xl bg-slate-800/40 border border-slate-700/20 rounded-2xl p-6">
                {/* Header */}
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-lg bg-slate-800 flex items-center justify-center">
                    <img
                      src={selectedConnector?.icon}
                      alt={selectedConnector?.name}
                      className="w-6 h-6 object-contain"
                    />
                  </div>
                  <div>
                    <h2 className="text-lg font-semibold text-white mb-1">Configure {selectedConnector?.name} Connection</h2>
                    <p className="text-gray-400 text-sm">Enter your connection details</p>
                  </div>
                </div>
                
                {/* Connection Name - Full Width */}
                <div className="mb-4">
                  <Label htmlFor="connectionName" className="text-gray-300 font-medium text-sm">Connection Name</Label>
                  <Input
                    id="connectionName"
                    name="connectionName"
                    value={credentials.connectionName}
                    onChange={handleCredentialChange}
                    placeholder="My Connection"
                    className="mt-1.5 bg-slate-950/[0.25] border border-slate-700/20 text-white placeholder:text-gray-500 h-10 focus:border-blue-500/50 focus:ring-0"
                  />
                </div>

                {/* Divider */}
                <div className="border-t border-slate-700/50 my-4"></div>

                {/* Section Header */}
                <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">Connection Details</p>

                {/* Organization Name & Account Name */}
                <div className="grid grid-cols-2 gap-4 mb-3">
                  <div>
                    <Label htmlFor="orgName" className="text-gray-300 font-medium text-sm">Organization Name</Label>
                    <Input
                      id="orgName"
                      name="orgName"
                      value={credentials.accountUrl}
                      onChange={handleCredentialChange}
                      placeholder="my-org"
                      className="mt-1.5 bg-slate-950/[0.25] border border-slate-700/20 text-white placeholder:text-gray-500 h-10 focus:border-blue-500/50 focus:ring-0"
                    />
                  </div>
                  <div>
                    <Label htmlFor="accountName" className="text-gray-300 font-medium text-sm">Account Name</Label>
                    <Input
                      id="accountName"
                      name="accountName"
                      value={credentials.warehouse}
                      onChange={handleCredentialChange}
                      placeholder="account-name"
                      className="mt-1.5 bg-slate-950/[0.25] border border-slate-700/20 text-white placeholder:text-gray-500 h-10 focus:border-blue-500/50 focus:ring-0"
                    />
                  </div>
                </div>

                {/* Warehouse & Database */}
                <div className="grid grid-cols-2 gap-4 mb-3">
                  <div>
                    <Label htmlFor="warehouse" className="text-gray-300 font-medium text-sm">Warehouse</Label>
                    <Input
                      id="warehouse"
                      name="warehouse"
                      value={credentials.warehouse}
                      onChange={handleCredentialChange}
                      placeholder="COMPUTE_WH"
                      className="mt-1.5 bg-slate-950/[0.25] border border-slate-700/20 text-white placeholder:text-gray-500 h-10 focus:border-blue-500/50 focus:ring-0"
                    />
                  </div>
                  <div>
                    <Label htmlFor="database" className="text-gray-300 font-medium text-sm">Database</Label>
                    <Input
                      id="database"
                      name="database"
                      value={credentials.database}
                      onChange={handleCredentialChange}
                      placeholder="MY_DATABASE"
                      className="mt-1.5 bg-slate-950/[0.25] border border-slate-700/20 text-white placeholder:text-gray-500 h-10 focus:border-blue-500/50 focus:ring-0"
                    />
                  </div>
                </div>

                {/* User - Half Width */}
                <div className="grid grid-cols-2 gap-4 mb-3">
                  <div>
                    <Label htmlFor="username" className="text-gray-300 font-medium text-sm">User</Label>
                    <Input
                      id="username"
                      name="username"
                      value={credentials.username}
                      onChange={handleCredentialChange}
                      placeholder="username"
                      className="mt-1.5 bg-slate-950/[0.25] border border-slate-700/20 text-white placeholder:text-gray-500 h-10 focus:border-blue-500/50 focus:ring-0"
                    />
                  </div>
                </div>

                {/* Password - Full Width */}
                <div className="mb-4">
                  <Label htmlFor="password" className="text-gray-300 font-medium text-sm">Password</Label>
                  <Input
                    id="password"
                    name="password"
                    type="password"
                    value={credentials.password}
                    onChange={handleCredentialChange}
                    placeholder="••••••••"
                    className="mt-1.5 bg-slate-950/[0.25] border border-slate-700/20 text-white placeholder:text-gray-500 h-10 focus:border-blue-500/50 focus:ring-0"
                  />
                </div>

                {/* Success Message */}
                {connectionSuccess && (
                  <div className="mb-4 p-3 bg-green-500/10 border border-green-500/30 rounded-lg flex items-center gap-2">
                    <Check className="w-4 h-4 text-green-400" />
                    <span className="text-green-400 text-sm">Connection successful!</span>
                  </div>
                )}

                {/* Action Buttons */}
                <div className="flex justify-between gap-3">
                  <Button
                    variant="outline"
                    onClick={() => setCurrentStep(1)}
                    className="border-slate-700 text-gray-300 hover:bg-slate-800 h-10 px-5"
                  >
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    Back
                  </Button>
                  <div className="flex gap-3">
                    {!connectionSuccess ? (
                      <Button
                        onClick={handleTestConnection}
                        disabled={isConnecting}
                        className="bg-blue-600 hover:bg-blue-700 text-white h-10 px-5"
                      >
                        {isConnecting ? (
                          <>
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            Testing...
                          </>
                        ) : (
                          'Test Connection'
                        )}
                      </Button>
                    ) : (
                      <Button
                        onClick={handleContinueToTables}
                        className="bg-blue-600 hover:bg-blue-700 text-white h-10 px-5"
                      >
                        Continue
                        <ArrowRight className="w-4 h-4 ml-2" />
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Step 3: Select Datasets - Full Width */}
        {currentStep === 3 && (
          <div 
            className="flex-1 flex flex-col relative overflow-hidden"
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseUp}
          >
            {/* Header */}
            <div className="px-6 pt-6 pb-4 flex-shrink-0">
              <h2 className="text-lg font-semibold text-white mb-1">Select Datasets</h2>
              <p className="text-gray-400 text-sm">Choose the datasets you want to include in your semantic graph</p>
            </div>
            
            {/* Resizable Panels */}
            <div className="flex-1 flex overflow-hidden">
              {/* Left Panel - Browse Schemas & Tables */}
              <div 
                className="h-full flex flex-col border-r border-slate-700/50"
                style={{ width: `${leftPanelWidth}%` }}
              >
                <div className="px-4 py-3 border-t border-b border-slate-700/50 flex items-center h-12 flex-shrink-0">
                  <div className="flex items-center gap-2 flex-1">
                    <Search className="w-4 h-4 text-gray-500" />
                    <input
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      placeholder="Search schemas or tables..."
                      className="bg-transparent border-none text-white placeholder:text-gray-500 text-sm focus:outline-none flex-1"
                    />
                  </div>
                </div>
                
                <div className="flex-1 p-3 pb-20 overflow-y-auto">
                  <div className="space-y-1">
                    {filteredSchemas.map((schema) => (
                      <div key={schema.name}>
                        <div 
                          className="flex items-center justify-between px-3 py-1.5 rounded-lg hover:bg-slate-800/50 cursor-pointer"
                          onClick={() => toggleSchema(schema.name)}
                        >
                          <div className="flex items-center gap-2">
                            {expandedSchemas.includes(schema.name) ? (
                              <ChevronDown className="w-4 h-4 text-gray-400" />
                            ) : (
                              <ChevronRight className="w-4 h-4 text-gray-400" />
                            )}
                            <Database className="w-4 h-4 text-blue-400" />
                            <span className="text-sm text-gray-200 font-mono">{schema.name}</span>
                          </div>
                          <span className="text-xs text-gray-500">
                            {getSelectedCountForSchema(schema.name)}/{schema.tables.length}
                          </span>
                        </div>
                        
                        {expandedSchemas.includes(schema.name) && (
                          <div className="ml-6 pl-4 border-l border-slate-700/50 mt-0.5 max-h-80 overflow-y-auto">
                            <div className="space-y-0">
                              {schema.tables.map((table) => (
                                <div
                                  key={table.name}
                                  className={`
                                    flex items-center justify-between px-3 py-1.5 rounded-lg cursor-pointer
                                    ${isTableSelected(schema.name, table.name) 
                                      ? 'hover:bg-slate-800/30' 
                                      : 'hover:bg-slate-800/30'}
                                  `}
                                  onClick={() => toggleTableSelection(schema.name, table.name)}
                                >
                                  <div className="flex items-center gap-2">
                                    <div className={`
                                      w-4 h-4 rounded border flex items-center justify-center
                                      ${isTableSelected(schema.name, table.name)
                                        ? 'bg-blue-600 border-blue-600'
                                        : 'border-slate-600'}
                                    `}>
                                      {isTableSelected(schema.name, table.name) && (
                                        <Check className="w-3 h-3 text-white" />
                                      )}
                                    </div>
                                    <span className="text-sm text-gray-300 font-mono">{table.name}</span>
                                  </div>
                                  <span className="text-xs text-gray-500">{table.rows}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Draggable Divider */}
              <div
                className="relative cursor-col-resize group z-30"
                onMouseDown={handleMouseDown}
                style={{ 
                  width: '16px',
                  margin: '0 -8px'
                }}
              >
                {/* Thin divider line with hover effect */}
                <div className="absolute left-1/2 -translate-x-1/2 w-[1px] h-full bg-slate-700/30 group-hover:bg-blue-500/60 transition-colors z-30" />
                
                {/* Pill-shaped handle in center */}
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[6px] h-6 bg-slate-700 group-hover:bg-blue-500 rounded-full transition-colors z-40" />
              </div>

              {/* Right Panel - Selected Tables */}
              <div 
                className="h-full flex flex-col"
                style={{ width: `${100 - leftPanelWidth}%` }}
              >
                <div className="px-4 py-3 border-t border-b border-slate-700/50 flex items-center justify-between h-12 flex-shrink-0">
                  <h3 className="text-sm font-medium text-gray-300">
                    Selected Datasets ({selectedTables.length})
                  </h3>
                  {selectedTables.length > 0 && (
                    <button 
                      className="text-xs text-gray-500 hover:text-gray-300"
                      onClick={() => setSelectedTables([])}
                    >
                      Clear All
                    </button>
                  )}
                </div>
                
                <div className="flex-1 overflow-y-auto pb-20">
                  {selectedTables.length === 0 ? (
                    <div className="flex flex-col items-center justify-center h-full text-gray-500">
                      <Database className="w-16 h-16 mb-4 opacity-20" />
                      <p className="text-sm">No datasets selected</p>
                      <p className="text-xs mt-1">Select datasets from the left panel</p>
                    </div>
                  ) : (
                    <div>
                      {/* Table Header */}
                      <div className="grid grid-cols-12 gap-1 px-4 py-2.5 text-[11px] font-medium text-gray-400 uppercase tracking-wider border-b border-slate-700/50 sticky top-0 bg-slate-900 shadow-sm z-10">
                        <div className="col-span-1 text-center">#</div>
                        <div className="col-span-2">Dataset Name</div>
                        <div className="col-span-2">Alias</div>
                        <div className="col-span-1 text-right">Rows</div>
                        <div className="col-span-1 text-right">Cols</div>
                        <div className="col-span-1 text-center pl-8">Format</div>
                        <div className="col-span-4 text-center">Actions</div>
                      </div>
                      
                      {/* Table Rows */}
                      {selectedTables.map((table, index) => (
                        <div 
                          key={table.id} 
                          className={`grid grid-cols-12 gap-1 px-4 py-2 text-sm items-center ${
                            index % 2 === 0 ? 'bg-slate-800/20' : 'bg-transparent'
                          } hover:bg-slate-800/40 transition-colors`}
                        >
                          <div className="col-span-1 text-gray-500 text-xs text-center">{index + 1}</div>
                          <div className="col-span-2 text-gray-200 font-mono text-xs truncate" title={table.name}>{table.name}</div>
                          <div className="col-span-2">
                            <input
                              value={table.alias}
                              onChange={(e) => {
                                setSelectedTables(prev => prev.map(t => 
                                  t.id === table.id ? { ...t, alias: e.target.value } : t
                                ));
                              }}
                              className="w-full text-[10px] px-1.5 py-0.5 bg-slate-700/30 text-gray-400 rounded text-left font-mono focus:outline-none focus:bg-slate-700/50 focus:text-gray-200"
                            />
                          </div>
                          <div className="col-span-1 text-gray-400 text-xs text-right">{table.rows}</div>
                          <div className="col-span-1 text-gray-400 text-xs text-right">{table.columns}</div>
                          <div className="col-span-1 text-center pl-8">
                            <span className="text-[10px] px-1.5 py-0.5 bg-slate-700/50 text-gray-400 rounded">Table</span>
                          </div>
                          <div className="col-span-4 flex items-center justify-center gap-0.5">
                            <button className="p-1 hover:bg-slate-700 rounded text-gray-500 hover:text-gray-200 transition-colors" title="Info">
                              <Info className="w-3.5 h-3.5" />
                            </button>
                            <button className="p-1 hover:bg-slate-700 rounded text-gray-500 hover:text-gray-200 transition-colors" title="Preview">
                              <Eye className="w-3.5 h-3.5" />
                            </button>
                            <button className="p-1 hover:bg-slate-700 rounded text-gray-500 hover:text-gray-200 transition-colors" title="Refresh">
                              <RefreshCw className="w-3.5 h-3.5" />
                            </button>
                            <button className="p-1 hover:bg-slate-700 rounded text-gray-500 hover:text-gray-200 transition-colors" title="Edit">
                              <Pencil className="w-3.5 h-3.5" />
                            </button>
                            <button 
                              className="p-1 hover:bg-red-500/20 rounded text-gray-500 hover:text-red-400 transition-colors" 
                              title="Remove"
                              onClick={() => setSelectedTables(prev => prev.filter(t => t.id !== table.id))}
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Overlay Footer Actions */}
            <div className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-slate-900 via-slate-900/95 to-transparent">
              <div className="flex justify-between">
                <Button
                  variant="outline"
                  onClick={() => setCurrentStep(2)}
                  className="border-slate-700 text-gray-300 hover:bg-slate-800"
                >
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back
                </Button>
                <Button
                  onClick={handleSaveConnection}
                  disabled={selectedTables.length === 0}
                  className="bg-blue-600 hover:bg-blue-700 text-white"
                >
                  Save Connection
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DataConnectionsPage;
